import React, { useState, useEffect, useRef } from 'react';
import {
  ChatBubbleLeftRightIcon, PaperAirplaneIcon
} from '@heroicons/react/24/outline';
import { useQuery, useMutation, usePagination } from '../../hooks';
import { chatAPI } from '../../services/api';
import {
  Card, SectionHeader, Button, Badge, StatusBadge,
  LoadingPage, EmptyState, Avatar,
} from '../../components/common/UI';
import { useSelector } from 'react-redux';
import { selectUser } from '../../store/slices/authSlice';
import { format } from 'date-fns';

const PRIORITY_BADGE = { low:'gray', normal:'blue', high:'yellow', urgent:'red' };

const ChatWindow = ({ room, onClose }) => {
  const [msg, setMsg]   = useState('');
  const me              = useSelector(selectUser);
  const bottomRef       = useRef(null);

  const { data, loading, refetch } = useQuery(
    () => chatAPI.messages(room.id, { limit: 100 }),
    [room.id]
  );
  const { mutate: send, loading: sending } = useMutation(
    () => chatAPI.sendMessage(room.id, { message: msg }),
    { errorMsg: false, onSuccess: () => { setMsg(''); refetch(); } }
  );

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [data]);

  const messages = Array.isArray(data) ? data : (data?.rows || []);
  const handleSend = () => { if (msg.trim()) send(); };
  const handleKey  = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  return (
    <div className="flex flex-col h-full">
      {/* Room header */}
      <div className="flex items-center justify-between border-b border-surface-100 p-4">
        <div>
          <p className="font-semibold text-surface-900">{room.subject || 'Support Chat'}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <StatusBadge status={room.status} />
            <Badge variant={PRIORITY_BADGE[room.priority]}>{room.priority}</Badge>
          </div>
        </div>
        <button onClick={onClose} className="btn btn-ghost p-1.5 rounded-lg text-surface-400">✕</button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? <LoadingPage /> : messages.length === 0 ? (
          <p className="text-center text-sm text-surface-400 py-8">No messages yet</p>
        ) : messages.map(m => {
          const isMine = m.sender_id === me?.id;
          return (
            <div key={m.id} className={`flex gap-2 ${isMine ? 'flex-row-reverse' : ''}`}>
              <Avatar name={`${m.sender?.first_name} ${m.sender?.last_name}`} size="sm" className="flex-shrink-0" />
              <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 text-sm ${isMine ? 'bg-brand-600 text-white rounded-tr-sm' : 'bg-surface-100 text-surface-800 rounded-tl-sm'}`}>
                {!isMine && <p className={`text-[10px] font-semibold mb-1 ${isMine ? 'text-brand-200' : 'text-brand-600'}`}>{m.sender?.first_name}</p>}
                <p>{m.message}</p>
                <p className={`text-[10px] mt-1 ${isMine ? 'text-brand-200' : 'text-surface-400'}`}>{m.created_at ? format(new Date(m.created_at), 'HH:mm') : ''}</p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="border-t border-surface-100 p-4 flex items-end gap-2">
        <textarea
          className="input flex-1 resize-none min-h-[40px] max-h-[120px]"
          placeholder="Type your message… (Enter to send)"
          value={msg}
          onChange={e => setMsg(e.target.value)}
          onKeyDown={handleKey}
          rows={1}
        />
        <Button variant="primary" size="md" icon={PaperAirplaneIcon} loading={sending} onClick={handleSend} disabled={!msg.trim()} />
      </div>
    </div>
  );
};

export function ChatPage() {
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [statusFilter, setStatusFilter] = useState('open');
  const { page, setPage } = usePagination();

  const { data, loading, refetch } = useQuery(
    () => chatAPI.adminRooms({ page, limit: 20, status: statusFilter || undefined }),
    [page, statusFilter]
  );

  const { mutate: assign } = useMutation(
    (rid) => chatAPI.assign(rid, {}),
    { successMsg: 'Room assigned to you', onSuccess: refetch }
  );
  const { mutate: close } = useMutation(
    (rid) => chatAPI.close(rid),
    { successMsg: 'Chat closed', onSuccess: refetch }
  );

  const rooms = data?.rows  || [];
  const total = data?.count || 0;

  return (
    <div className="animate-in">
      <SectionHeader title="Support Chat" subtitle={`${total} conversations`} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-180px)]">
        {/* Rooms list */}
        <Card className="overflow-hidden flex flex-col">
          <div className="p-3 border-b border-surface-100">
            <div className="flex gap-1">
              {['open','in_progress','resolved','closed'].map(s => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors ${statusFilter === s ? 'bg-brand-600 text-white' : 'text-surface-500 hover:bg-surface-100'}`}>
                  {s.replace(/_/g,' ')}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? <LoadingPage /> : rooms.length === 0 ? (
              <EmptyState icon={ChatBubbleLeftRightIcon} title="No chats" />
            ) : rooms.map(r => (
              <button key={r.id} onClick={() => setSelectedRoom(r)}
                className={`w-full text-left p-3 border-b border-surface-50 hover:bg-surface-50 transition-colors ${selectedRoom?.id === r.id ? 'bg-brand-50' : ''}`}>
                <div className="flex items-start gap-2">
                  <Avatar name={r.customer?.first_name || 'C'} size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-surface-900 truncate">{r.customer?.first_name} {r.customer?.last_name}</p>
                    <p className="text-xs text-surface-400 truncate">{r.subject || 'No subject'}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <StatusBadge status={r.status} />
                      <Badge variant={PRIORITY_BADGE[r.priority]}>{r.priority}</Badge>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Chat window */}
        <div className="lg:col-span-2">
          {selectedRoom ? (
            <Card className="h-full overflow-hidden p-0">
              <ChatWindow room={selectedRoom} onClose={() => setSelectedRoom(null)} />
            </Card>
          ) : (
            <div className="flex h-full items-center justify-center text-surface-400">
              <div className="text-center">
                <ChatBubbleLeftRightIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Select a conversation</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Re-export CategoriesPage from business/Categories ────
export { default as CategoriesPage } from '../business/Categories';

// ── Re-export InquiriesPage from business/Inquiry ────
export { default as InquiriesPage } from '../business/Inquiry';
